import { getApiBaseUrl } from './apiConfig';

const VIEW_KEYS = ['front', 'left', 'right'];

/** Presigned S3/E2E URLs return encrypted bytes — not displayable in the browser. */
export function isUnusableE2eUrl(value) {
  if (!value || typeof value !== 'string') return false;
  const raw = value.trim();
  if (!raw || raw.startsWith('data:') || raw.startsWith('blob:')) return false;
  if (raw.startsWith('skin/')) return true;
  if (raw.includes('X-Amz-') || raw.includes('x-amz-')) return true;
  if (/objectstore\.e2enetworks\.net/i.test(raw)) return true;
  return false;
}

export function reportImagesNeedHydration(images = {}) {
  return VIEW_KEYS.some((view) => {
    const url = images[view];
    return !url || isUnusableE2eUrl(url);
  });
}

export function reportImagesMissingViews(images = {}) {
  return VIEW_KEYS.some((view) => !images[view]);
}

export function mergeReportImages(primary = {}, fallback = {}) {
  const out = { front: null, left: null, right: null };
  VIEW_KEYS.forEach((view) => {
    const primaryUrl = primary[view];
    const fallbackUrl = fallback[view];
    if (fallbackUrl && (!primaryUrl || isUnusableE2eUrl(primaryUrl))) {
      out[view] = resolveE2eMediaUrl(fallbackUrl);
    } else {
      out[view] = resolveE2eMediaUrl(primaryUrl);
    }
  });
  return out;
}

/**
 * Resolve E2E / API media references to a browser-loadable URL.
 * Backend report payloads should already presign skin/ keys; this handles relative paths and aliases.
 */
export function resolveE2eMediaUrl(value) {
  if (value == null) return null;
  if (typeof value !== 'string') return null;
  const raw = value.trim();
  if (!raw) return null;
  if (
    raw.startsWith('data:')
    || raw.startsWith('http://')
    || raw.startsWith('https://')
    || raw.startsWith('blob:')
  ) {
    return raw;
  }
  if (raw.startsWith('/')) {
    const base = getApiBaseUrl();
    return base ? `${base.replace(/\/$/, '')}${raw}` : raw;
  }
  return raw;
}

export function normalizeReportImages(images = {}) {
  const source = images && typeof images === 'object' ? images : {};
  const out = { front: null, left: null, right: null };
  VIEW_KEYS.forEach((view) => {
    const candidate = source[view]
      ?? source[`${view}_image`]
      ?? source[`${view}Image`]
      ?? null;
    out[view] = resolveE2eMediaUrl(candidate);
  });
  if (!out.front) {
    out.front = resolveE2eMediaUrl(
      source.captured
      || source.captured_image
      || source.capturedImage
      || source.marked_image
      || source.markedImage
      || null,
    );
  }
  return out;
}

export function normalizeReportMasks(masks = {}) {
  if (!masks || typeof masks !== 'object') return {};
  const out = {};
  Object.entries(masks).forEach(([key, val]) => {
    out[key] = resolveE2eMediaUrl(val);
  });
  return out;
}

export function normalizeSkinReportPayload(data) {
  if (!data || data.success === false) return data;
  const { success, error, ...payload } = data;
  return {
    ...payload,
    success: success !== false,
    images: normalizeReportImages(payload.images || {}),
    visual_masks: normalizeReportMasks(payload.visual_masks || {}),
  };
}

/**
 * Load image for canvas display. Avoids crossOrigin by default so presigned E2E URLs work without CORS.
 */
export function loadDisplayImage(src) {
  return new Promise((resolve, reject) => {
    if (!src) {
      reject(new Error('missing src'));
      return;
    }
    const img = new window.Image();
    img.onload = () => resolve(img);
    img.onerror = () => {
      const retry = new window.Image();
      retry.crossOrigin = 'anonymous';
      retry.onload = () => resolve(retry);
      retry.onerror = () => reject(new Error('image load failed'));
      retry.src = src;
    };
    img.src = src;
  });
}
